import {
    Component,
    ElementRef,
    forwardRef,
    Input,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// SESSION-admin-ptg-003 — shared `.seq-edit-row` repeater for AR presentation
// sequences. CVA over SequenceRow[]; auto-renumbers 01/02/… on add/remove.
// Mirrors SequenceDto (Index/Name/RuntimeSecs) on the wire.
//
// SESSION-meet-ux-seq-scroll — the row list lives in a `.seq-scroll` box capped
// at ~6 rows. Long presentations (the C-arm deck ships 14 sequences) used to
// grow the host modal past the viewport and push its footer buttons off-screen.
// `addRow` scrolls the new row into view so the cap stays invisible in use.
export interface SequenceRow {
    name: string;
    runtimeSecs: number;
    index?: number;
}

@Component({
    selector: 'app-sequence-row-repeater',
    templateUrl: './sequence-row-repeater.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SequenceRowRepeaterComponent),
            multi: true,
        },
    ],
})
export class SequenceRowRepeaterComponent implements ControlValueAccessor {
    @Input() namePlaceholder = 'Sequence name';
    @Input() runtimePlaceholder = '~3 min';

    @ViewChild('scrollBox') scrollBox?: ElementRef<HTMLElement>;

    rows: SequenceRow[] = [];

    // Set by addRow so the post-render hook knows to reveal the new last row.
    private pendingScrollToEnd = false;

    private onChange: (v: SequenceRow[]) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(v: SequenceRow[]): void {
        this.rows = Array.isArray(v) ? v.map((r) => ({ ...r })) : [];
        this.renumber();
    }

    registerOnChange(fn: (v: SequenceRow[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    // True when the newest row is still unnamed. Blocks stacking blank rows —
    // an empty sequence is meaningless on the wire and used to persist as one.
    get addDisabled(): boolean {
        if (!this.rows.length) {
            return false;
        }
        return !(this.rows[this.rows.length - 1].name || '').trim();
    }

    addRow(): void {
        if (this.addDisabled) {
            // Nothing to add yet — surface the offending row instead.
            this.pendingScrollToEnd = true;
            this.revealLastRow();
            return;
        }
        this.rows = [...this.rows, { name: '', runtimeSecs: 180 }];
        this.renumber();
        this.emit();
        this.pendingScrollToEnd = true;
        // Defer past the *ngFor re-render so the new row's height is measurable.
        setTimeout(() => this.revealLastRow());
    }

    // Scrolls the newest row into view and focuses its name input, so a row
    // added past the visible cap is not silently created below the fold.
    private revealLastRow(): void {
        if (!this.pendingScrollToEnd) {
            return;
        }
        this.pendingScrollToEnd = false;
        const box = this.scrollBox?.nativeElement;
        if (!box) {
            return;
        }
        box.scrollTop = box.scrollHeight;
        const rows = box.querySelectorAll<HTMLInputElement>(
            '.seq-edit-row input'
        );
        // 2 inputs per row; the name field of the last row is at -2.
        const nameInput = rows.item(rows.length - 2);
        if (nameInput) {
            nameInput.focus();
        }
    }

    removeRow(index: number): void {
        this.rows.splice(index, 1);
        this.rows = [...this.rows];
        this.renumber();
        this.emit();
    }

    updateName(index: number, name: string): void {
        this.rows[index].name = name;
        this.emit();
    }

    // Accepts free-text minutes like "~3 min" or "5 min"; persists as seconds.
    updateRuntime(index: number, value: string): void {
        this.rows[index].runtimeSecs = this.parseMinutes(value);
        this.emit();
    }

    runtimeText(secs: number): string {
        if (!secs) {
            return '';
        }
        const mins = Math.max(1, Math.round(secs / 60));
        return `~${mins} min`;
    }

    trackByIndex(index: number): number {
        return index;
    }

    padded(i: number): string {
        return String(i + 1).padStart(2, '0');
    }

    private renumber(): void {
        this.rows = this.rows.map((r, i) => ({ ...r, index: i }));
    }

    private parseMinutes(s: string): number {
        const match = /(\d+(?:\.\d+)?)/.exec(s || '');
        if (!match) {
            return 0;
        }
        const mins = parseFloat(match[1]);
        return Math.round(mins * 60);
    }

    private emit(): void {
        this.onChange(this.rows);
        this.onTouched();
    }
}
